import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { I18nContext } from './context';
import { createTranslator, LOCALE_HTML_LANG } from './translator';
import type { Locale } from './types';

export interface I18nProviderProps {
  readonly locale: Locale;
  readonly children: ReactNode;
}

/**
 * Publishes the translator for the active locale.
 *
 * The locale itself lives in the settings store; this component only turns it
 * into a translator and keeps the document in sync, which keeps the i18n layer
 * free of any dependency on how preferences are stored.
 */
export function I18nProvider({ locale, children }: I18nProviderProps) {
  const translator = useMemo(() => createTranslator(locale), [locale]);

  // Screen readers pick pronunciation from `lang`, so it has to follow the UI.
  useEffect(() => {
    document.documentElement.lang = LOCALE_HTML_LANG[locale];
  }, [locale]);

  return <I18nContext value={translator}>{children}</I18nContext>;
}
